import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import { Navigation, Layers, Radio } from 'lucide-react';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const MapView = ({ flights = [], selectedFlight, onSelectFlight, airport }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const tileLayerRef = useRef(null);
  const [tileLayer, setTileLayer] = useState('satellite');
  const lastSentRef = useRef(new Map());
  const pendingSendRef = useRef(new Set());

  const airportLat = airport?.latitude ?? airport?.lat ?? 29.7947;
  const airportLon = airport?.longitude ?? airport?.lon ?? -98.7318;
  const airportName = airport?.name ?? 'Airport';
  const airportIcao = airport?.icao_code ?? airport?.icao ?? 'KBOE';

  const nearbyAirports = [
    { icao: 'KSAT', name: 'San Antonio Intl', lat: 29.5337, lon: -98.4698 },
    { icao: 'KAUS', name: 'Austin-Bergstrom', lat: 30.1945, lon: -97.6699 },
  ];

  const tileLayers = {
    satellite: {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles &copy; Esri &mdash; Maxar, GeoEye, Earthstar Geographics',
    },
    osm: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  };

  // Init map once
  useEffect(() => {
    if (mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: [airportLat, airportLon],
      zoom: 12,
      zoomControl: true,
    });

    tileLayerRef.current = L.tileLayer(tileLayers.satellite.url, {
      attribution: tileLayers.satellite.attribution,
    }).addTo(map);

    const mainIcon = L.divIcon({
      html: `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" fill="#F5A623" opacity="0.2" stroke="#F5A623" stroke-width="2"/>
        <line x1="12" y1="4" x2="12" y2="20" stroke="#F5A623" stroke-width="2.5" stroke-linecap="round"/>
        <line x1="4" y1="12" x2="20" y2="12" stroke="#F5A623" stroke-width="2.5" stroke-linecap="round"/>
      </svg>`,
      className: '', iconSize: [32, 32], iconAnchor: [16, 16],
    });

    L.marker([airportLat, airportLon], { icon: mainIcon })
      .addTo(map)
      .bindPopup(`
        <div style="background:#0F1F38;color:#fff;padding:8px 10px;border-radius:8px;min-width:130px;font-family:monospace">
          <div style="color:#F5A623;font-weight:700;font-size:12px">${airportIcao}</div>
          <div style="font-size:11px;color:#CBD5E1;margin-top:2px">${airportName}</div>
          <div style="font-size:9px;color:#64748B;margin-top:4px">${airportLat.toFixed(4)}, ${airportLon.toFixed(4)}</div>
        </div>`, { className: 'custom-popup' });

    nearbyAirports.forEach(ap => {
      const icon = L.divIcon({
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" fill="#60A5FA" opacity="0.2" stroke="#60A5FA" stroke-width="2"/>
          <line x1="12" y1="4" x2="12" y2="20" stroke="#60A5FA" stroke-width="2.5" stroke-linecap="round"/>
          <line x1="4" y1="12" x2="20" y2="12" stroke="#60A5FA" stroke-width="2.5" stroke-linecap="round"/>
        </svg>`,
        className: '', iconSize: [22, 22], iconAnchor: [11, 11],
      });
      L.marker([ap.lat, ap.lon], { icon })
        .addTo(map)
        .bindPopup(`
          <div style="background:#0F1F38;color:#fff;padding:8px 10px;border-radius:8px;font-family:monospace">
            <div style="color:#60A5FA;font-weight:700;font-size:12px">${ap.icao}</div>
            <div style="font-size:11px;color:#CBD5E1">${ap.name}</div>
          </div>`, { className: 'custom-popup' });
    });

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
      markersRef.current = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Switch tile layer
  useEffect(() => {
    if (!mapInstanceRef.current || !tileLayerRef.current) return;
    tileLayerRef.current.remove();
    const active = tileLayers[tileLayer];
    tileLayerRef.current = L.tileLayer(active.url, { attribution: active.attribution })
      .addTo(mapInstanceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tileLayer]);

  // Update flight markers
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    const map = mapInstanceRef.current;
    const currentIds = new Set(flights.map(f => f.id || f.icao24 || f.tail_number));

    Object.keys(markersRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        markersRef.current[id].remove();
        delete markersRef.current[id];
      }
    });

    flights.forEach(flight => {
      if (!flight.latitude || !flight.longitude) return;
      const id = flight.id || flight.icao24 || flight.tail_number;
      const isSelected = selectedFlight?.id === flight.id;
      const color = isSelected ? '#60A5FA' : flight.on_ground ? '#10B981' : '#F59E0B';
      const size = isSelected ? 36 : 28;

      const icon = L.divIcon({
        html: `<div style="width:${size}px;height:${size}px;transform:rotate(${flight.heading || 0}deg);transform-origin:center;${isSelected ? `filter:drop-shadow(0 0 6px ${color})` : ''}">
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 100 100">
            <ellipse cx="50" cy="50" rx="7" ry="38" fill="${color}" stroke="white" stroke-width="2"/>
            <ellipse cx="50" cy="52" rx="44" ry="9" fill="${color}" stroke="white" stroke-width="2"/>
            <ellipse cx="50" cy="82" rx="22" ry="5" fill="${color}" stroke="white" stroke-width="1.5"/>
            <circle cx="50" cy="14" r="5" fill="white" opacity="0.9"/>
          </svg>
        </div>`,
        className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2],
      });

      const popupContent = `
        <div style="background:#0F1F38;color:#fff;padding:8px 10px;border-radius:8px;min-width:160px;font-family:monospace">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px">
            <span style="color:${color};font-weight:700;font-size:12px">${flight.tail_number || flight.callsign || 'Unknown'}</span>
            <span style="background:${flight.on_ground ? '#064E3B' : '#78350F'};color:${flight.on_ground ? '#10B981' : '#F59E0B'};font-size:9px;padding:2px 5px;border-radius:4px;font-weight:600">
              ${flight.on_ground ? 'GROUND' : 'AIRBORNE'}
            </span>
          </div>
          <table style="width:100%;font-size:10px;border-collapse:collapse">
            <tr><td style="color:#64748B;padding-bottom:2px;padding-right:10px">Callsign</td><td style="color:#E2E8F0;text-align:right">${flight.callsign || 'N/A'}</td></tr>
            <tr><td style="color:#64748B;padding-bottom:2px;padding-right:10px">Altitude</td><td style="color:#E2E8F0;text-align:right">${flight.altitude ? Math.round(flight.altitude) + ' ft' : 'N/A'}</td></tr>
            <tr><td style="color:#64748B;padding-bottom:2px;padding-right:10px">Speed</td><td style="color:#E2E8F0;text-align:right">${flight.velocity ? Math.round(flight.velocity) + ' kts' : 'N/A'}</td></tr>
            <tr><td style="color:#64748B;padding-bottom:2px;padding-right:10px">Heading</td><td style="color:#E2E8F0;text-align:right">${flight.heading ? Math.round(flight.heading) + '°' : 'N/A'}</td></tr>
          </table>
        </div>`;

      if (markersRef.current[id]) {
        markersRef.current[id].setLatLng([flight.latitude, flight.longitude]);
        markersRef.current[id].setIcon(icon);
        markersRef.current[id].getPopup()?.setContent(popupContent);
      } else {
        const marker = L.marker([flight.latitude, flight.longitude], { icon })
          .addTo(map)
          .bindPopup(popupContent, { className: 'custom-popup' });
        marker.on('click', () => onSelectFlight && onSelectFlight(isSelected ? null : flight));
        markersRef.current[id] = marker;
      }
    });
  }, [flights, selectedFlight, onSelectFlight]);

  // Fly to selected flight
  useEffect(() => {
    if (!mapInstanceRef.current || !selectedFlight?.latitude) return;
    mapInstanceRef.current.flyTo(
      [selectedFlight.latitude, selectedFlight.longitude],
      Math.max(mapInstanceRef.current.getZoom(), 13),
      { duration: 1.2 }
    );
  }, [selectedFlight]);

  // Tracking ingest
  const sendTrackingPoint = useCallback(async (flight) => {
    const flightId = flight.id || flight.icao24 || flight.tail_number;
    if (pendingSendRef.current.has(flightId)) return;
    const now = Date.now();
    const lastSent = lastSentRef.current.get(flightId);
    if (lastSent && now - lastSent < 30000) return;
    if (!flight.latitude || !flight.longitude) return;
    const tailNumber = flight.tail_number || flight.callsign;
    if (!tailNumber || tailNumber === 'N/A') return;

    pendingSendRef.current.add(flightId);
    try {
      await axios.post(`${API_BASE}/tracking/ingest`, {
        tail_number: tailNumber,
        latitude: flight.latitude,
        longitude: flight.longitude,
        altitude: Math.round(flight.altitude || 0),
        speed: Math.round(flight.velocity || 0),
        heading: flight.heading || 0,
        on_ground: flight.on_ground || false,
        timestamp: new Date().toISOString(),
      });
      lastSentRef.current.set(flightId, now);
      if (lastSentRef.current.size > 100)
        lastSentRef.current.delete(lastSentRef.current.keys().next().value);
    } catch (err) {
      if (err.response?.status !== 404)
        console.debug('Tracking send error:', err.response?.data?.reason || err.message);
    } finally {
      pendingSendRef.current.delete(flightId);
    }
  }, []);

  useEffect(() => {
    if (!flights.length) return;
    const sendAll = async () => { for (const f of flights) await sendTrackingPoint(f); };
    sendAll();
  }, [flights, sendTrackingPoint]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', fontFamily: "'JetBrains Mono', monospace" }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {/* Airport info - Responsive positioning */}
      <div style={{
        position: 'absolute', top: 12, left: 12, zIndex: 1000,
        background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(8px)',
        borderRadius: 10, padding: '6px 10px',
        border: '1px solid rgba(59,130,246,0.25)',
        display: 'flex', alignItems: 'center', gap: 8,
        pointerEvents: 'none',
      }}>
        <div style={{ background: 'rgba(245,166,35,0.15)', borderRadius: 6, padding: 6 }}>
          <Navigation size={12} color="#F5A623" />
        </div>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: 0.5 }}>{airportName}</div>
          <div style={{ color: '#64748B', fontSize: 9, marginTop: 1 }}>
            {airportIcao} · {airportLat.toFixed(4)}, {airportLon.toFixed(4)}
          </div>
        </div>
      </div>

      {/* Tile toggle - Responsive: stack on mobile, row on desktop */}
      <div style={{
        position: 'absolute', top: 12, right: 12, zIndex: 1000,
        display: 'flex', flexDirection: 'row', gap: 6,
      }}>
        {[['satellite', 'Satellite'], ['osm', 'Map']].map(([key, label]) => (
          <button key={key} onClick={() => setTileLayer(key)} style={{
            background: tileLayer === key ? 'rgba(59,130,246,0.8)' : 'rgba(10,22,40,0.85)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${tileLayer === key ? '#3B82F6' : 'rgba(59,130,246,0.25)'}`,
            borderRadius: 6, padding: '4px 8px', color: '#fff',
            fontSize: 10, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.2s',
          }}>
            <Layers size={10} />{label}
          </button>
        ))}
      </div>

      {/* Legend - Responsive wrap on small screens */}
      <div style={{
        position: 'absolute', bottom: 12, left: 12, zIndex: 1000,
        background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(8px)',
        borderRadius: 8, padding: '6px 10px',
        border: '1px solid rgba(59,130,246,0.2)',
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        pointerEvents: 'none',
      }}>
        {[
          { color: '#10B981', label: 'Ground' },
          { color: '#F59E0B', label: 'Airborne' },
          { color: '#F5A623', label: airportIcao },
          { color: '#60A5FA', label: 'Nearby' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
            <span style={{ color: '#94A3B8', fontSize: 9, fontWeight: 500 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Flight count */}
      <div style={{
        position: 'absolute', bottom: 12, right: 12, zIndex: 1000,
        background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(8px)',
        borderRadius: 8, padding: '6px 10px',
        border: '1px solid rgba(59,130,246,0.2)',
        display: 'flex', alignItems: 'center', gap: 6,
        pointerEvents: 'none',
      }}>
        <Radio size={10} color="#10B981" />
        <span style={{ color: '#94A3B8', fontSize: 9 }}>
          <span style={{ color: '#fff', fontWeight: 700 }}>{flights.length}</span> aircraft
        </span>
      </div>

      <style>{`
        .leaflet-container { background: #0A1628; }
        .leaflet-pane         { z-index: 4 !important; }
        .leaflet-tile-pane    { z-index: 2 !important; }
        .leaflet-overlay-pane { z-index: 3 !important; }
        .leaflet-shadow-pane  { z-index: 4 !important; }
        .leaflet-marker-pane  { z-index: 5 !important; }
        .leaflet-tooltip-pane { z-index: 6 !important; }
        .leaflet-popup-pane   { z-index: 7 !important; }
        .leaflet-control      { z-index: 8 !important; }
        .custom-popup .leaflet-popup-content-wrapper { background: transparent; box-shadow: none; padding: 0; border: none; }
        .custom-popup .leaflet-popup-tip-container { display: none; }
        .custom-popup .leaflet-popup-content { margin: 0; }
        .leaflet-control-attribution { background: rgba(10,22,40,0.7) !important; color: #475569 !important; font-size: 8px !important; }
        .leaflet-control-attribution a { color: #60A5FA !important; }
        
        /* Responsive popup adjustments */
        @media (max-width: 640px) {
          .leaflet-popup-content {
            min-width: 140px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MapView;